import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useTasks, useView, useCreateView, useUpdateView, useDeleteView } from '@/hooks/useTasks.ts';
import { FilterBar } from '@/components/FilterBar.tsx';
import { Board, type BoardGrouping } from '@/components/Board.tsx';
import { TaskDrawer } from '@/components/TaskDrawer.tsx';
import { QuickCapture } from '@/components/QuickCapture.tsx';
import { PageHeader, Button, Select } from '@/components/ui.tsx';
import type { TaskFilter } from '@shared/types';

/** Board rows are ordered by hand, so the server sort must not fight the drag. */
const BOARD_FILTER: TaskFilter = { top_level_only: true };

/**
 * Kanban board. Shares the saved-view machinery with the table: a board is
 * just a view whose `kind` is 'board'.
 */
export function BoardView() {
  const { id: viewId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const openTaskId = searchParams.get('task');

  const { data: savedView } = useView(viewId ?? null);
  const createView = useCreateView();
  const updateView = useUpdateView();
  const deleteView = useDeleteView();

  const [filter, setFilter] = useState<TaskFilter>(BOARD_FILTER);
  const [grouping, setGrouping] = useState<BoardGrouping>('status');

  useEffect(() => {
    if (savedView) {
      setFilter(savedView.filter);
      setGrouping(savedView.group_by === 'project_id' ? 'project_id' : 'status');
    } else if (!viewId) {
      setFilter(BOARD_FILTER);
      setGrouping('status');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedView?.id, viewId]);

  // No sort argument: the board's order IS the position column, and asking the
  // server to sort by anything else would undo a drag on the next refetch.
  const { data: tasks, isPending, isError } = useTasks(filter);

  function openTask(id: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('task', id);
      return next;
    });
  }

  function closeTask() {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('task');
      return next;
    });
  }

  function handleSave() {
    const name = prompt('Name this board:', savedView?.name ?? '');
    if (!name?.trim()) return;

    if (savedView) {
      updateView.mutate({ id: savedView.id, name: name.trim(), filter, group_by: grouping });
    } else {
      createView.mutate(
        { name: name.trim(), kind: 'board', filter, sorts: [], group_by: grouping },
        { onSuccess: (view) => navigate(`/board/${view.id}`) },
      );
    }
  }

  return (
    <div className="mx-auto w-full max-w-[1440px] px-5 py-8 sm:px-8">
      <PageHeader
        title={savedView?.name ?? 'Board'}
        subtitle="Drag a card to move it between columns."
        {...(tasks ? { count: `${tasks.length} shown` } : {})}
        actions={
          <div className="flex items-center gap-1.5">
            <span className="w-44">
              <Select
                value={grouping}
                onChange={(v) => setGrouping(v as BoardGrouping)}
                ariaLabel="Column grouping"
                align="right"
                options={[
                  { value: 'status', label: 'Columns by status' },
                  { value: 'project_id', label: 'Columns by project' },
                ]}
              />
            </span>
            <Button variant="primary" onClick={handleSave}>
              {savedView ? 'Update board' : 'Save board'}
            </Button>
            {savedView && (
              <Button
                variant="danger"
                onClick={() => {
                  if (confirm(`Delete the board "${savedView.name}"? Your tasks are not affected.`)) {
                    deleteView.mutate(savedView.id, { onSuccess: () => navigate('/board') });
                  }
                }}
              >
                Delete
              </Button>
            )}
          </div>
        }
      />

      <QuickCapture defaultStatus="inbox" />

      <FilterBar filter={filter} onChange={setFilter} />

      {isPending && (
        <p className="text-sm" style={{ color: 'var(--color-ink-2)' }}>
          Loading…
        </p>
      )}

      {isError && (
        <p role="alert" className="text-sm" style={{ color: 'var(--color-brick)' }}>
          Could not reach the server.
        </p>
      )}

      {tasks && <Board tasks={tasks} grouping={grouping} onOpen={openTask} />}

      {openTaskId && <TaskDrawer taskId={openTaskId} onClose={closeTask} />}
    </div>
  );
}
